interface Segment {
  content: string;
  color?: string;
  size?: number;
  ruby?: string;
  romanHanging?: boolean;
}

export function parseMarkdownToJson(sourceText: string): Segment[] {
  const colorPattern = /\{([^{}]+)\|([^{}]+)\}/;
  const rubyPattern = /\[([^\[\]]+)\]\(([^\(\)]+)\)/;
  const romanHangingPattern = /\<\<([^<>]+)\>\>/;

  function parse(text: string, color?: string, size?: number, ruby?: string): Segment[] {
    const result: Segment[] = [];
    let remainingText = text;

    while (remainingText) {
      const colorMatch = remainingText.match(colorPattern);
      const rubyMatch = remainingText.match(rubyPattern);
      const romanHangingMatch = remainingText.match(romanHangingPattern);

      const matches = [
        { type: 'color', match: colorMatch },
        { type: 'ruby', match: rubyMatch },
        { type: 'romanHanging', match: romanHangingMatch }
      ].filter(m => m.match).sort((a, b) => a.match!.index! - b.match!.index!);

      if (matches.length > 0) {
        const { type, match } = matches[0];
        const [fullMatch, content, param] = match!;
        const index = match!.index!;

        if (index > 0) {
          result.push({ content: remainingText.slice(0, index), color, size, ruby });
        }

        switch (type) {
          case 'color':
            // パラメータをコンマで分割して色とサイズを取得
            const params = param.split(',').map(p => p.trim());
            const newColor = params[0] || undefined;
            const newSize = params[1] ? parseFloat(params[1]) : undefined;
            result.push(...parse(content, newColor || color, newSize || size, ruby));
            break;
          case 'ruby':
            result.push(...parse(content, color, size, param));
            break;
          case 'romanHanging':
            result.push({ content, color, size, ruby, romanHanging: true });
            break;
        }

        remainingText = remainingText.slice(index + fullMatch.length);
      } else {
        result.push({ content: remainingText, color, size, ruby });
        remainingText = '';
      }
    }

    return result;
  }

  return parse(sourceText);
}

export interface RichFragment {
  chars: string[];
  color?: string;
  size?: number;
  ruby?: string[];
  romanHanging?: boolean;
}

function isHalfWidth(char: string): boolean {
  return /^[\u0020-\u007E\uFF61-\uFF9F]$/.test(char);
}

function getCompleteEmojiSequence(text: string, index: number): string | null {
  const regex = /(\p{Emoji}(\uFE0F|\u200D\p{Emoji})*)/u;
  const match = text.slice(index).match(regex);
  if (match && match.index === 0 && !isHalfWidth(match[0])) {
    return match[0];
  }
  // 2文字目が合字半濁点・濁点の場合は合成文字として扱う
  if (text.length > index + 1) {
    // console.log(`\\u${text.charCodeAt(index + 1).toString(16).padStart(4, '0')}`)
    if (text[index + 1] === '\u3099' || text[index + 1] === '\u309A') {
      return text.slice(index, index + 2);
    }
/*
    横書きと一貫性がないので一旦保留
    if (text[index + 1] === "゛") {
      return text.slice(index, index + 1) + '\u3099';
    } else if (text[index + 1] === "゜") {
      return text.slice(index, index + 1) + '\u309A';
    }
*/
  }
  return null;
}

function* characterGroupIterator(text: string): Generator<string, void, unknown> {
  let current = '';
  let isCurrentHalfWidth = false;
  let i = 0;

  while (i < text.length) {
    const char = text[i];

    if (isHalfWidth(char)) {
      if (isCurrentHalfWidth) {
        current += char;
      } else {
        if (current) yield current;
        current = char;
        isCurrentHalfWidth = true;
      }
      i++;
    } else {
      const emojiSequence = getCompleteEmojiSequence(text, i);
      if (emojiSequence) {
        if (current) yield current;
        yield emojiSequence;
        current = '';
        isCurrentHalfWidth = false;
        i += emojiSequence.length;
      } else {
        if (current) yield current;
        yield char;
        current = '';
        isCurrentHalfWidth = false;
        i++;
      }
    }
  }

  if (current) yield current;
}

export function* richTextIterator(segments: Segment[]): Generator<RichFragment, void, unknown> {
  for (const segment of segments) {
    const chars = [...characterGroupIterator(segment.content)];
    
    if (segment.ruby) {
      yield {
        chars: chars,
        color: segment.color,
        size: segment.size,
        ruby: [...characterGroupIterator(segment.ruby)],
        romanHanging: segment.romanHanging
      };
    } else if (segment.romanHanging) {
      yield {
        chars: [segment.content],
        color: segment.color,
        size: segment.size,
        romanHanging: segment.romanHanging
      };
    } else {
      for (const char of chars) {
        yield {
          chars: [char],
          color: segment.color,
          size: segment.size,
          romanHanging: segment.romanHanging
        };
      }
    }
  }
}

/*
// 使用例
const segments: Segment[] = [
  { content: "これは" },
  { content: "赤い", color: "red" },
  { content: "文字", ruby: "もじ" },
  { content: "と" },
  { content: "ABC", romanHanging: true },
  { content: "絵文字😊と" },
  { content: "Latin1 text", romanHanging: true },
  { content: "そしてひらがな" },
  { content: "赤いABCだよ" }   
];

console.log([...richTextIterator(segments)]);

[
  { "chars": ["こ"], "color": undefined, "romanHanging": undefined },
  { "chars": ["れ"], "color": undefined, "romanHanging": undefined },
  { "chars": ["は"], "color": undefined, "romanHanging": undefined },
  { "chars": ["赤"], "color": "red", "romanHanging": undefined },
  { "chars": ["い"], "color": "red", "romanHanging": undefined },
  { "chars": ["文", "字"], "color": undefined, "ruby": ["も", "じ"], "romanHanging": undefined },
  { "chars": ["と"], "color": undefined, "romanHanging": undefined },
  { "chars": ["ABC"], "color": undefined, "romanHanging": true },
  { "chars": ["絵"], "color": undefined, "romanHanging": undefined },
  { "chars": ["文"], "color": undefined, "romanHanging": undefined },
  { "chars": ["字"], "color": undefined, "romanHanging": undefined },
  { "chars": ["😊"], "color": undefined, "romanHanging": undefined },
  { "chars": ["と"], "color": undefined, "romanHanging": undefined },
  { "chars": ["Latin1 text"], "color": undefined, "romanHanging": true },
  { "chars": ["そ"], "color": undefined, "romanHanging": undefined },
  { "chars": ["し"], "color": undefined, "romanHanging": undefined },
  { "chars": ["て"], "color": undefined, "romanHanging": undefined },
  { "chars": ["ひ"], "color": undefined, "romanHanging": undefined },
  { "chars": ["ら"], "color": undefined, "romanHanging": undefined },
  { "chars": ["が"], "color": undefined, "romanHanging": undefined },
  { "chars": ["な"], "color": undefined, "romanHanging": undefined },
  { "chars": ["赤"], "color": undefined, "romanHanging": undefined },
  { "chars": ["い"], "color": undefined, "romanHanging": undefined },
  { "chars": ["ABC"], "color": undefined, "romanHanging": undefined },
  { "chars": ["だ"], "color": undefined, "romanHanging": undefined },
  { "chars": ["よ"], "color": undefined, "romanHanging": undefined }
]
*/